import { Text, tokens } from "@ui"
import { css, keyframes } from "dx-styles"
import type { FC, ReactNode } from "react"

const auraSpin = keyframes({
  from: { transform: "rotate(0deg)" },
  to: { transform: "rotate(360deg)" },
})

const auraBreathe = keyframes({
  "0%, 100%": { transform: "scale(0.92)", opacity: 0.65 },
  "50%": { transform: "scale(1.06)", opacity: 1 },
})

const wrapper = css({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: tokens.space.sm,
  width: "100%",
  minHeight: "60vh",
  paddingBlock: tokens.space.lg,
  overflow: "hidden",
})

const auraOrbit = css({
  position: "absolute",
  insetBlockStart: "50%",
  insetInlineStart: "50%",
  width: "520px",
  height: "520px",
  marginBlockStart: "-290px",
  marginInlineStart: "-260px",
  pointerEvents: "none",
  animation: `${auraSpin} 32s linear infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const aura = css({
  width: "100%",
  height: "100%",
  filter: "blur(4px)",
  backgroundImage: [
    "radial-gradient(44% 44% at 32% 64%, rgba(250, 176, 72, 0.9) 0%, rgba(250, 176, 72, 0.45) 34%, rgba(250, 176, 72, 0) 70%)",
    "radial-gradient(36% 36% at 60% 74%, rgba(240, 150, 162, 0.5) 0%, rgba(240, 150, 162, 0) 72%)",
    "radial-gradient(46% 46% at 70% 38%, rgba(199, 150, 245, 0.85) 0%, rgba(199, 150, 245, 0.4) 40%, rgba(199, 150, 245, 0) 74%)",
    "radial-gradient(40% 40% at 34% 32%, rgba(214, 180, 250, 0.6) 0%, rgba(214, 180, 250, 0) 76%)",
  ].join(", "),
  animation: `${auraBreathe} 7s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const stage = css({
  position: "relative",
  width: "220px",
  height: "220px",
})

const copy = css({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: tokens.space.xs,
  textAlign: "center",
  maxWidth: "36ch",
})

export type IllustratedStateProps = {
  title: string
  description: string
  children: ReactNode
}

export const IllustratedState: FC<IllustratedStateProps> = ({
  title,
  description,
  children,
}) => {
  return (
    <div
      className={wrapper}
      role="status"
      aria-live="polite"
    >
      <div className={auraOrbit}>
        <div className={aura} />
      </div>

      <div className={stage}>{children}</div>

      <div className={copy}>
        <Text variant="h3">{title}</Text>
        <Text variant="description">{description}</Text>
      </div>
    </div>
  )
}
